import { createFileRoute, Link } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowRight } from "lucide-react";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { RelatedReading } from "@/components/RelatedReading";
import { relatedArticles } from "@/lib/clusters";
import { buildBlogHead } from "@/lib/blogMeta";
import { CATALOG, imageUrlFor } from "@/lib/ucp/catalog";
import manuscript from "@/content/sgot-and-sgpt-normal-range.md?raw";
import hero from "@/assets/blog/sgot-hero-1600.webp.asset.json";
import heroSmall from "@/assets/blog/sgot-hero-768.webp.asset.json";
import normal from "@/assets/blog/sgot-normal-1600.webp.asset.json";
import normalSmall from "@/assets/blog/sgot-normal-768.webp.asset.json";
import causes from "@/assets/blog/sgot-causes-1600.webp.asset.json";
import causesSmall from "@/assets/blog/sgot-causes-768.webp.asset.json";
import whatToDo from "@/assets/blog/sgot-whattodo-1600.webp.asset.json";
import whatToDoSmall from "@/assets/blog/sgot-whattodo-768.webp.asset.json";
import faqImage from "@/assets/blog/sgot-faq-1600.webp.asset.json";
import faqSmall from "@/assets/blog/sgot-faq-768.webp.asset.json";

const SLUG = "sgot-and-sgpt-normal-range";
const TITLE = "SGOT and SGPT Normal Range: What High Liver Enzymes Mean | TruWe";
const DESCRIPTION =
  "SGOT and SGPT normal range by age and sex, what high levels mean, danger levels and when to see a doctor. Simple tables and guidance from TruWe.";
const HEADLINE = "SGOT and SGPT Normal Range: What High Liver Enzymes Mean";
const PUBLISHED = "2026-10-22";
const images: Record<string, { url: string; small: string }> = {
  "sgot-normal": { url: normal.url, small: normalSmall.url },
  "sgot-causes": { url: causes.url, small: causesSmall.url },
  "sgot-whattodo": { url: whatToDo.url, small: whatToDoSmall.url },
  "sgot-faq": { url: faqImage.url, small: faqSmall.url },
};

export const Route = createFileRoute("/blog/sgot-and-sgpt-normal-range")({
  component: Article,
  head: () => {
    const base = buildBlogHead({
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      ogTitle: HEADLINE,
      ogDescription: DESCRIPTION,
      headline: HEADLINE,
      breadcrumbName: "SGOT and SGPT Normal Range",
      datePublished: PUBLISHED,
      dateModified: PUBLISHED,
      authorName: "TruWe",
      articleType: "BlogPosting",
      faqs: [],
      ogImage: `https://truwe.in${hero.url}`,
    });
    return {
      ...base,
      meta: [
        ...base.meta,
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: `https://truwe.in${hero.url}` },
      ],
    };
  },
});

function ProductSpotlight() {
  const sku = CATALOG.find((p) => p.handle === "liver-biome-plus");
  const tier = sku?.tiers.find((t) => t.days === 30) ?? sku?.tiers[0];
  return (
    <aside
      aria-label="Liver Biome Plus"
      className="mt-6 flex flex-col gap-5 rounded-2xl border border-brand/20 bg-gradient-to-br from-brand/5 via-background to-brand-deep/5 p-5 sm:flex-row sm:items-center md:p-6"
    >
      <img
        src={imageUrlFor("liver-biome-plus")}
        alt="TruWe Liver Biome Plus bottle"
        width={160}
        height={160}
        loading="eager"
        decoding="async"
        className="h-32 w-32 shrink-0 self-center rounded-xl bg-card object-contain"
      />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-brand">Related TruWe formula</p>
        <h2 className="mt-1 font-display text-xl font-bold text-brand-deep">Liver Biome Plus</h2>
        <p className="mt-1 text-sm leading-6 text-foreground/80">
          Liposomal silymarin, curcumin and ALA with NAC and a gut biome stack, for daily liver support.
          A food supplement, not a treatment for abnormal results.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Link
            to="/products/$handle"
            params={{ handle: "liver-biome-plus" }}
            className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-brand-deep active:scale-[0.98]"
          >
            View Liver Biome Plus <ArrowRight className="h-4 w-4" />
          </Link>
          {tier && (
            <span className="text-sm font-semibold text-brand-deep">
              ₹{Math.round(tier.pricePaise / 100).toLocaleString("en-IN")}
            </span>
          )}
        </div>
      </div>
    </aside>
  );
}

function Article() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <p className="text-xs font-semibold uppercase text-brand">Clinical Library · Liver</p>
        <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight text-brand-deep md:text-5xl">
          {HEADLINE}
        </h1>
        <ProductSpotlight />
        <BlogByline
          author="TruWe"
          date={PUBLISHED}
          readingTime="9 min read"
          sources="MedlinePlus lab test guides and peer-reviewed reference-range research."
        />
        <div className="mt-4"><ReadWithLlm title={HEADLINE} url={`https://truwe.in/blog/${SLUG}`} kind="article" /></div>
        <img
          src={hero.url}
          srcSet={`${heroSmall.url} 768w, ${hero.url} 1600w`}
          sizes="(max-width: 768px) 100vw, 768px"
          alt="SGOT and SGPT normal range: what high liver enzymes mean"
          width={1600}
          height={859}
          fetchPriority="high"
          decoding="async"
          className="mt-8 h-auto w-full rounded-md border border-border"
        />
        <div className="mt-8 text-base leading-8 text-foreground/85 md:text-[17px]">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
            h2: ({ children }) => <h2 className="mb-4 mt-12 font-display text-2xl font-extrabold leading-tight text-brand-deep md:text-3xl">{children}</h2>,
            h3: ({ children }) => <h3 className="mb-3 mt-8 font-display text-xl font-bold text-brand-deep">{children}</h3>,
            p: ({ children }) => <p className="my-4">{children}</p>,
            ul: ({ children }) => <ul className="my-5 list-disc space-y-2 pl-6 marker:text-brand">{children}</ul>,
            li: ({ children }) => <li className="pl-1">{children}</li>,
            strong: ({ children }) => <strong className="font-semibold text-brand-deep">{children}</strong>,
            a: ({ href, children }) => <a href={href} rel={href?.startsWith("http") ? "noopener noreferrer" : undefined} target={href?.startsWith("http") ? "_blank" : undefined} className="text-brand underline underline-offset-2 hover:text-brand-deep">{children}</a>,
            img: ({ src, alt }) => {
              const image = images[src ?? ""];
              if (!image) return null;
              return <img src={image.url} srcSet={`${image.small} 768w, ${image.url} 1600w`} sizes="(max-width: 768px) 100vw, 768px" alt={alt ?? ""} width={1600} height={859} loading="lazy" decoding="async" className="my-8 h-auto w-full rounded-md border border-border" />;
            },
            table: ({ children }) => <div className="my-6 overflow-x-auto border-y border-border"><table className="w-full min-w-[560px] border-collapse text-left text-sm leading-6">{children}</table></div>,
            th: ({ children }) => <th className="border-b border-border bg-muted/30 px-4 py-3 font-semibold text-brand-deep">{children}</th>,
            td: ({ children }) => <td className="border-b border-border px-4 py-3 align-top">{children}</td>,
          }}>{manuscript.replace(/^# .+\n/, "")}</ReactMarkdown>
        </div>
        <RelatedReading articles={relatedArticles(SLUG)} />
      </div>
    </article>
  );
}
